import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {useTenant} from '../App';
import {publicApi} from '../api';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

export default function JobDetail() {
  const {id} = useParams();
  const tenant = useTenant();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  usePageTitle(job?.title, job ? `Apply for ${job.title} at ${tenant.name}` : undefined);

  useEffect(() => {
    publicApi(`/ats/public/jobs/${id}?tenantId=${tenant.id}`).then(r => r.ok ? r.json() : null).then(setJob).finally(() => setLoading(false));
  }, [id, tenant.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const fd = new FormData(e.target);
    fd.set('tenantId', tenant.id);
    fd.set('jobId', id);
    try {
      const res = await publicApi('/ats/public/apply', {method: 'POST', body: fd});
      if (res.ok) { navigate('/applied'); }
      else { setError('Something went wrong. Please try again.'); }
    } catch {
      setError('Unable to submit. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Spinner className="flex-1 py-20" />
      <Footer />
    </div>
  );

  if (!job) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center fade-in">
        <div className="text-4xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h1>
        <p className="text-gray-500 mb-4">This position may have been filled or removed.</p>
        <Link to="/" className="text-teal-600 hover:text-teal-800 font-medium focus:outline-none focus:underline">← Back to all positions</Link>
      </div>
    </div>
  );

  const input = "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/" className="text-teal-600 hover:text-teal-800 text-sm focus:outline-none focus:underline">← {tenant.name} — All Positions</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex-1 w-full">
        <article className="bg-white rounded-lg shadow p-6 sm:p-8 mb-6 fade-in">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{job.title}</h1>
          <div className="flex flex-wrap gap-2 sm:gap-3 text-sm text-gray-500 mb-6">
            {job.department && <span className="bg-gray-100 px-3 py-1 rounded-full">{job.department}</span>}
            {job.location && <span className="bg-gray-100 px-3 py-1 rounded-full">📍 {job.location}</span>}
            {job.employmentType && <span className="bg-gray-100 px-3 py-1 rounded-full">{job.employmentType.replace('_', ' ')}</span>}
            {(job.salaryMin || job.salaryMax) && (
              <span className="bg-gray-100 px-3 py-1 rounded-full">
                {job.salaryMin ? `$${Number(job.salaryMin).toLocaleString()}` : ''}
                {job.salaryMin && job.salaryMax ? ' – ' : ''}
                {job.salaryMax ? `$${Number(job.salaryMax).toLocaleString()}` : ''}
              </span>
            )}
          </div>
          {job.description && <div className="prose prose-gray max-w-none text-gray-700" dangerouslySetInnerHTML={{__html: job.description}} />}
        </article>

        {!showForm ? (
          <div className="text-center fade-in">
            <button onClick={() => setShowForm(true)} className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 font-medium text-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2">
              Apply Now
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 sm:p-8 fade-in" id="apply-form">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Apply for {job.title}</h2>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2" role="alert">
                <span>⚠️</span> {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input id="firstName" name="firstName" required autoComplete="given-name" className={input} />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input id="lastName" name="lastName" required autoComplete="family-name" className={input} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input id="email" name="email" type="email" autoComplete="email" placeholder="you@example.com" className={input} />
                  <p className="text-xs text-gray-400 mt-1">We'll send you a link to track your application.</p>
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input id="phone" name="phone" type="tel" autoComplete="tel" className={input} />
                </div>
              </div>
              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">Resume</label>
                <input id="resume" name="resume" type="file" accept=".pdf,.doc,.docx" className="text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 file:cursor-pointer" />
                <p className="text-xs text-gray-400 mt-1">PDF, DOC, or DOCX (max 10MB)</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" disabled={submitting} className="bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 font-medium disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2">
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Submitting...
                    </span>
                  ) : 'Submit Application'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
